import { useEffect, useMemo, useRef, useState } from "react";
import { EditableMathField, addStyles as addMathQuillStyles } from "react-mathquill";

function getStatusMeta(status) {
  if (status === "incorrect_but_right_direction") return { label: "Fix", className: "status-incomplete" };
  if (status === "wrong_direction") return { label: "Off path", className: "status-incorrect" };
  if (status === "dependent_on_error") return { label: "Blocked", className: "status-review" };
  if (status === "ahead_of_frontier") return { label: "Later", className: "status-unclear" };
  if (status === "superseded") return { label: "Replaced", className: "status-review" };
  if (status === "incorrect") return { label: "Incorrect", className: "status-incorrect" };
  if (status === "incomplete") return { label: "Incomplete", className: "status-incomplete" };
  if (status === "missing_step") return { label: "Missing", className: "status-missing" };
  if (status === "unclear") return { label: "Unclear", className: "status-unclear" };
  if (status === "correct") return { label: "Correct", className: "status-correct" };
  return { label: "Review", className: "status-review" };
}

function isStructuralLatexLine(line) {
  const text = String(line || "").trim();
  if (!text) return true;
  if (/^\\(begin|end)\{/.test(text)) return true;
  if (/^\\(left|right|bigl|bigr|Bigl|Bigr|big|Big)\s*[\\{}[\]().|]+$/.test(text)) return true;
  if (/^[{}[\]().|]+$/.test(text)) return true;
  const stripped = text
    .replace(/\\(left|right|bigl|bigr|Bigl|Bigr|big|Big)/g, "")
    .replace(/[{}[\]().|]/g, "")
    .trim();
  return stripped.length === 0;
}

export function sanitizeLatexForMathQuill(line) {
  if (!line || typeof line !== "string") return "";
  let clean = line.trim();

  // Strip markdown fences, dollar signs, and math delimiters
  clean = clean
    .replace(/```[a-zA-Z]*\n?/g, "")
    .replace(/```/g, "")
    .replace(/\\\(/g, "")
    .replace(/\\\)/g, "")
    .replace(/\\\[/g, "")
    .replace(/\\\]/g, "")
    .replace(/\$/g, "");

  // Remove LaTeX environments that break line-by-line MathQuill
  const envPattern =
    "(?:align\\*?|gather\\*?|aligned|equation\\*?|multline\\*?|cases|array|matrix|pmatrix|bmatrix|vmatrix|smallmatrix)";
  clean = clean
    .replace(new RegExp(`\\\\begin\\{${envPattern}\\}`, "g"), "")
    .replace(new RegExp(`\\\\end\\{${envPattern}\\}`, "g"), "")
    .replace(/&/g, "");

  // Remove/normalize spacing commands that MathQuill's parser fails on
  // MathQuill lacks LatexCmds for \, \; \: \! \thinspace etc, causing silent parser failures & empty fields
  clean = clean
    .replace(/(?<!\\)\\(?:,|;|:|!|thinspace|medspace|thickspace|enspace)(?![a-zA-Z])/g, " ")
    .replace(/\\quad\b/g, " ")
    .replace(/\\qquad\b/g, " ");

  // Normalize differentials & standard operators
  clean = clean
    .replace(/\\(?:mathrm|text|operatorname)\{d\}/g, "d")
    .replace(/\\operatorname\{([a-zA-Z]+)\}/g, "\\$1");

  // Clean stray/unpaired \left. or \right.
  clean = clean
    .replace(/\\(?:left|right)\./g, "")
    .replace(/\\(?:bigl|bigr|Bigl|Bigr|big|Big)([()[\]|])/g, "$1");

  // Clean multiple consecutive whitespace
  clean = clean.replace(/[ \t]+/g, " ").trim();

  return clean;
}

function MathLiveEditor({
  value,
  onChange,
  readOnly = false,
  showKeyboard = false,
  stepDiagnostics = {},
  selectedStep = null,
  onStepSelect = null
}) {
  const mathRefs = useRef([]);
  const [activeLine, setActiveLine] = useState(0);

  useEffect(() => {
    addMathQuillStyles();
  }, []);

  const lines = useMemo(() => {
    const raw = String(value || "");
    const split = raw
      .split(/\s*\\\\\s*|\n+|(?<=\S)\s*\\\s+(?=[A-Za-z0-9_]+\s*=|=[^=])/)
      .map((line) => sanitizeLatexForMathQuill(line))
      .filter((line) => line.length > 0)
      .filter((line) => !isStructuralLatexLine(line));
    return split.length ? split : [""];
  }, [value]);

  function updateLine(index, nextValue) {
    const nextLines = [...lines];
    nextLines[index] = nextValue;
    if (onChange) {
      onChange(nextLines.join(" \\\\ "));
    }
  }

  function applyCommand(command) {
    const field = mathRefs.current[activeLine];
    if (!field || readOnly) return;
    field.focus();
    if (command.type === "cmd") field.cmd(command.value);
    if (command.type === "write") field.write(command.value);
    if (command.type === "typedText") field.typedText(command.value);
  }

  const tools = [
    { label: "frac", type: "cmd", value: "\\frac" },
    { label: "sqrt", type: "cmd", value: "\\sqrt" },
    { label: "int", type: "cmd", value: "\\int" },
    { label: "sum", type: "cmd", value: "\\sum" },
    { label: "lim", type: "cmd", value: "\\lim" },
    { label: "sin", type: "cmd", value: "\\sin" },
    { label: "cos", type: "cmd", value: "\\cos" },
    { label: "tan", type: "cmd", value: "\\tan" },
    { label: "pi", type: "cmd", value: "\\pi" },
    { label: "theta", type: "cmd", value: "\\theta" },
    { label: "alpha", type: "cmd", value: "\\alpha" },
    { label: "beta", type: "cmd", value: "\\beta" },
    { label: "gamma", type: "cmd", value: "\\gamma" },
    { label: "infty", type: "cmd", value: "\\infty" },
    { label: "x^2", type: "write", value: "x^2" },
    { label: "x_n", type: "write", value: "x_n" },
    { label: "<=", type: "cmd", value: "\\le" },
    { label: ">=", type: "cmd", value: "\\ge" },
    { label: "!=", type: "cmd", value: "\\ne" },
    { label: "->", type: "cmd", value: "\\to" },
    { label: "+/-", type: "cmd", value: "\\pm" },
    { label: "=", type: "typedText", value: "=" },
    { label: "+", type: "typedText", value: "+" },
    { label: "-", type: "typedText", value: "-" },
    { label: "(", type: "typedText", value: "(" },
    { label: ")", type: "typedText", value: ")" }
  ];

  return (
    <div className="math-editor-field">
      {!readOnly && showKeyboard && (
        <div className="math-editor-toolbar">
          {tools.map((tool) => (
            <button
              key={tool.label}
              type="button"
              className="tool-chip"
              onClick={() => applyCommand(tool)}
            >
              {tool.label}
            </button>
          ))}
        </div>
      )}
      <div className="math-editor-lines">
        {lines.map((line, index) => (
          <div key={index} className="math-editor-step-block">
            {(() => {
              const stepNo = index + 1;
              const diagnostic = stepDiagnostics[stepNo] || null;
              const statusMeta = getStatusMeta(diagnostic?.status);
              const rowClassName = [
                "math-editor-line",
                diagnostic ? statusMeta.className : "",
                selectedStep === stepNo ? "is-selected" : ""
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <>
                  <div className="math-editor-row">
                    <div
                      className={rowClassName}
                      onMouseDown={() => setActiveLine(index)}
                      onFocus={() => setActiveLine(index)}
                    >
                      <EditableMathField
                        readOnly={readOnly}
                        latex={line}
                        onChange={(mathField) => {
                          if (!mathField || typeof mathField.latex !== "function") return;
                          updateLine(index, mathField.latex());
                        }}
                        mathquillDidMount={(mathField) => {
                          mathRefs.current[index] = mathField;
                        }}
                        config={{
                          autoCommands: "pi theta sqrt sum int alpha beta gamma",
                          autoOperatorNames: "sin cos tan log ln",
                          restrictMismatchedBrackets: true
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      className={`math-step-tag ${diagnostic ? statusMeta.className : ""} ${
                        selectedStep === stepNo ? "is-selected" : ""
                      }`}
                      onClick={() => {
                        if (!onStepSelect) return;
                        onStepSelect(selectedStep === stepNo ? null : stepNo);
                      }}
                    >
                      <span>Step {stepNo}</span>
                      {diagnostic && <span className="math-step-badge">{statusMeta.label}</span>}
                    </button>
                  </div>
                  {diagnostic && selectedStep === stepNo && (
                    <div className={`inline-step-diagnostic ${statusMeta.className}`}>
                      {diagnostic.reason || "No issue noted for this step."}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        ))}
      </div>
      {readOnly && <div className="question-meta">Read-only mode</div>}
    </div>
  );
}

export default MathLiveEditor;
